import React, { useEffect, useRef, useState, useCallback } from 'react';
import { usePoseDetection } from '../hooks/usePoseDetection';
import { drawLandmarks, drawConnections, clearCanvas } from '../utils/drawingUtils';
import Avatar3D from './Avatar3D';
import { Slider } from './ui/slider';
import { Camera, CameraOff } from 'lucide-react';

const WebcamFeed = ({ onPoseUpdate, isActive, cameraOpacity = 0.3, viewMode = 'avatar' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [fps, setFps] = useState(0);
  const [opacity, setOpacity] = useState(cameraOpacity);
  const [smoothedLandmarks, setSmoothedLandmarks] = useState([]);
  const fpsCounterRef = useRef({ count: 0, lastTime: Date.now() });
  const onPoseUpdateRef = useRef(onPoseUpdate);
  onPoseUpdateRef.current = onPoseUpdate;
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;
  const lastLandmarkUpdateRef = useRef(0);
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;
  const streamRef = useRef(null);

  const { detectPose, isInitialized } = usePoseDetection({
    modelComplexity: 0,
    minDetectionConfidence: 0.5,
    minTrackingConfidence: 0.5
  });

  const detectPoseRef = useRef(detectPose);
  detectPoseRef.current = detectPose;
  const isInitializedRef = useRef(isInitialized);
  isInitializedRef.current = isInitialized;

  // Request camera permission immediately on mount
  useEffect(() => {
    requestCameraPermission();
    return () => releaseCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start/stop pose detection based on isActive + camera + model ready
  useEffect(() => {
    if (isActive && isCameraActive && isInitialized) {
      startDetectionLoop();
    } else {
      stopDetectionLoop();
    }
    return () => stopDetectionLoop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, isCameraActive, isInitialized]);

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }
      });
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setIsCameraActive(true);
        };
      }
    } catch (error) {
      console.error('Camera access denied:', error);
    }
  };

  const startDetectionLoop = () => {
    if (animationFrameRef.current) return; // already running
    runDetection();
  };

  const stopDetectionLoop = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const runDetection = async () => {
    if (!videoRef.current || !isInitializedRef.current || !isActiveRef.current) {
      animationFrameRef.current = null;
      setDebugInfo(`loop blocked: video=${!!videoRef.current} init=${isInitializedRef.current} active=${isActiveRef.current}`);
      return;
    }

    const video = videoRef.current;
    const currentMode = viewModeRef.current;

    try {
      const results = await detectPoseRef.current(video);

      // Camera mode: draw video + skeleton to canvas
      if (currentMode === 'camera' && canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        clearCanvas(ctx);

        ctx.save();
        ctx.scale(-1, 1);
        ctx.globalAlpha = opacity;
        ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
        ctx.globalAlpha = 1.0;
        ctx.restore();

        if (results?.landmarks?.[0]) {
          const landmarks = results.landmarks[0];

          ctx.save();
          ctx.scale(-1, 1);
          ctx.translate(-canvas.width, 0);
          drawConnections(ctx, landmarks, null, '#39FF14', 3);
          drawLandmarks(ctx, landmarks, '#00F0FF', 5);
          ctx.restore();

          if (onPoseUpdateRef.current) {
            onPoseUpdateRef.current(landmarks);
          }
        }
      }

      // Avatar mode: pass raw normalized landmarks to 3D avatar
      if (currentMode === 'avatar') {
        if (results?.landmarks?.[0]) {
          const landmarks = results.landmarks[0];

          // Throttle React state updates to ~30fps
          const now = performance.now();
          if (now - lastLandmarkUpdateRef.current > 33) {
            lastLandmarkUpdateRef.current = now;
            setSmoothedLandmarks(landmarks);
          }

          if (onPoseUpdateRef.current) {
            onPoseUpdateRef.current(landmarks);
          }
        }
      }

      updateFPS();
    } catch (error) {
      console.error('Pose detection error:', error);
    }

    if (isActiveRef.current) {
      animationFrameRef.current = requestAnimationFrame(runDetection);
    } else {
      animationFrameRef.current = null;
    }
  };

  const updateFPS = () => {
    const now = Date.now();
    fpsCounterRef.current.count++;

    if (now - fpsCounterRef.current.lastTime >= 1000) {
      setFps(fpsCounterRef.current.count);
      fpsCounterRef.current.count = 0;
      fpsCounterRef.current.lastTime = now;
    }
  };

  const releaseCamera = () => {
    stopDetectionLoop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const isAvatar = viewMode === 'avatar';

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <video
        ref={videoRef}
        className="hidden"
        playsInline
        muted
      />

      {!isAvatar && (
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="pose-canvas"
        />
      )}

      {isAvatar && isCameraActive && (
        <Avatar3D landmarks={smoothedLandmarks} />
      )}

      {isCameraActive && (
        <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md rounded-lg p-3 border border-primary/30">
          <div className="flex items-center gap-3 mb-2">
            <Camera className="w-4 h-4 text-primary" />
            <span className="text-xs text-primary font-semibold">FPS: {fps}</span>
          </div>
          {!isAvatar && (
            <div className="flex items-center gap-2">
              <CameraOff className="w-3 h-3 text-muted-foreground" />
              <Slider
                value={[opacity]}
                onValueChange={(val) => setOpacity(val[0])}
                min={0}
                max={1}
                step={0.1}
                className="w-20"
              />
              <Camera className="w-3 h-3 text-muted-foreground" />
            </div>
          )}
        </div>
      )}

      {!isInitialized && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center">
            <div className="animate-pulse-neon text-primary text-lg font-heading">LOADING MODEL...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebcamFeed;
